import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { useChatInstancesStore } from '../../../store/chatInstancesStore';
import { useBackgroundExecutionStore } from '../../../store/backgroundExecutionStore';
import { hasActiveChat } from '../../../store/chat-instance-presence';
import {
  isRecoverableStreamError,
  isUnreachableChatSendError,
  normalizeChatError,
} from '../../../utils/chatNetworkErrors';
import { isTerminalResumeSuppressed } from '../terminal-resume-suppress';

const SEND_MESSAGE_RETRY_INTERVAL_MS = 100;
const SEND_MESSAGE_MAX_RETRIES = 5;

export const UNREACHABLE_CHAT_RETRY_DELAYS_MS = [
  1_000, 2_000, 5_000, 10_000, 20_000, 30_000, 30_000, 30_000, 30_000,
] as const;

type SendAttemptOutcome = 'sent' | 'failed' | 'chat-unreachable';

export const isPortalToolSessionSuppressed = (sessionId: string): boolean =>
  useBackgroundExecutionStore.getState().isResumeSuppressed(sessionId);

const readUnreachableChatError = (sessionId: string): Error | undefined => {
  const chat = useChatInstancesStore.getState().chats.get(sessionId);

  return chat?.status === 'error' && isUnreachableChatSendError(chat.error)
    ? chat.error
    : undefined;
};

async function attemptSend(
  sessionId: string,
  message: TerraUIMessage,
): Promise<SendAttemptOutcome> {
  if (!hasActiveChat(sessionId)) {
    return 'failed';
  }

  const { sendMessageToChat } = useChatInstancesStore.getState();

  try {
    await sendMessageToChat(sessionId, message);
  } catch (error: unknown) {
    const normalized = normalizeChatError(error);

    console.warn('[PortalToolProvider] sendMessage failed:', error);

    if (isUnreachableChatSendError(normalized)) {
      return 'chat-unreachable';
    }

    if (isRecoverableStreamError(normalized)) {
      return 'sent';
    }

    return 'failed';
  }

  const unreachableError = readUnreachableChatError(sessionId);

  if (unreachableError) {
    console.warn(
      '[PortalToolProvider] chat was unreachable after send; retrying until it is back',
      unreachableError,
    );

    return 'chat-unreachable';
  }

  return 'sent';
}

export async function sendMessageWithRetry(
  sessionId: string,
  message: TerraUIMessage,
  retries = SEND_MESSAGE_MAX_RETRIES,
): Promise<boolean> {
  let failedAttempts = 0;
  let unreachableAttempts = 0;

  for (;;) {
    if (isTerminalResumeSuppressed(sessionId)) {
      return false;
    }

    if (isPortalToolSessionSuppressed(sessionId)) {
      return true;
    }

    const outcome = await attemptSend(sessionId, message);

    if (outcome === 'sent') {
      return true;
    }

    const delayMs =
      outcome === 'chat-unreachable'
        ? UNREACHABLE_CHAT_RETRY_DELAYS_MS[unreachableAttempts++]
        : ++failedAttempts < retries
          ? SEND_MESSAGE_RETRY_INTERVAL_MS
          : undefined;

    if (delayMs === undefined) {
      console.warn(
        `[PortalToolProvider] Failed to send external tool results after ${failedAttempts + unreachableAttempts} attempts for session ${sessionId}`,
      );

      return false;
    }

    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
}
